/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React, {
  ComponentPropsWithoutRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useHistory, useLocation } from '@bbkShared/react-router';
import { useSelector } from 'react-redux';
import { Slide, Tooltip } from '@mui/material';
import { cs } from '@bbkAdminUtils/css-utils';
import { css } from '@emotion/react';
import { isUndefined, set } from 'lodash';
import { produce } from 'immer';
import type { ReactFCC } from '@bbkAdminUtils/utility-types';
import { GenericPopover } from '@bbkAdminComponents/popovers/generic-popover';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import Icons from '@bbkAdminUtils/icons';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import type { SelectOption } from '@bbkAdminComponents/select/select';
import GenericSelect from '@bbkAdminComponents/select/select';
import { useUrl } from '@bbkAdminUtils/nav';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import type { Layout } from '@bbkAdminComponents/experiences/offers/new-offer-flow';
import {
  actions,
  layouts,
  NewOfferFlowContext,
} from '@bbkAdminComponents/experiences/offers/new-offer-flow';
import {
  OfferCategory,
  OfferObjective,
  OfferPresentation,
  RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  encodeObjectForQuery,
  isDefined,
} from '@bbkAdminRedux/rtkq/rtkq-utils';
import { useSelectNewOfferGoals } from '@bbkAdminRedux/rtkq/offers-templates.slice';
import type {
  CtaDetails,
  RestModalCard,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  CtaProcessingOption,
  RestModalAction,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import {
  CATEGORIES_DONT_ALLOW_BILLING_PROCESSING,
  useProcessingOpts,
} from '@bbkAdminComponents/experiences/offers/edit-offer-hooks';
import {
  renderCategoryIcon,
  replaceBrightbackWithDomainInOffer,
  updateCtaAction,
} from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import { externalLinkIcon } from '@bbkAdminComponents/switches/toggle/toggle';
import { selectAppId, selectCompany } from '@bbkAdminRedux/app/selectors';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import {
  isModalTemplate,
  isOfferCardPack,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { CSSObjectWithLabel } from 'react-select';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { OFFER_OBJECTIVES_CATEGORY_MAPPING } from '@bbkAdminUtils/offers';
import OfferObjectiveCard from '@bbkAdminComponents/experiences/offers/new-offer-flow-2022/offer-objective-card';
import { getObjectives } from '@bbkAdminUtils/offer-play-objectives';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

const numberButtonsOpts: SelectOption<number>[] = [
  {
    value: 2,
    label: 2,
  },
  {
    value: 3,
    label: 3,
  },
];

type Props = {
  open: boolean;
  onClose: CallableFunction;
};

type OfferPresentationCardArgs = {
  title: string;
  helpText: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};
export const OfferPresentationCard: React.FC<OfferPresentationCardArgs> = ({
  title,
  helpText,
  icon,
  onClick,
  disabled,
}) => {
  return (
    <button
      className={cs('tw-mx-4 tw-flex tw-flex-col', {
        'tw-cursor-not-allowed': disabled,
        'tw-cursor-pointer': !disabled,
      })}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="tw-rounded-md tw-border-[1px] tw-border-[#82C4E1] tw-px-8 tw-py-3">
        {icon}
      </div>
      <div
        className={cs('tw-w-full tw-text-center', {
          'tw-text-cbrneutral-300': disabled,
        })}
      >
        <div>
          <span>{title}</span>
          <TippyTooltip
            content={helpText}
            tippyOptions={{ placement: 'bottom' }}
          >
            <span className="info-icon tw-ml-2">{Icons.cbrInfo}</span>
          </TippyTooltip>
        </div>
      </div>
    </button>
  );
};

export const NewOfferChooseCategory: ReactFCC<Props> = ({
  open,
  onClose,
  children,
}) => {
  const history = useHistory();
  const location = useLocation<{
    aiFlow: boolean;
    fromPlayEditor?: boolean;
    objective?: OfferObjective;
    presentation?: OfferPresentation;
  }>();
  const searchParams = useSearchParams();
  const aiFlow = Boolean(location.state?.aiFlow);
  const createUrl = useUrl();
  const company = useSelector(selectCompany);
  const appKey = useSelector(selectAppId);
  const allOffers = useSelectAllOffers({ appKey });
  const offerPackCount = allOffers?.filter((o) => isOfferCardPack(o)).length;
  const enableHostedPages = useFeatureFlags(
    FeatureFlagsE.ENABLE_LIFECYCLE_HOSTED_PAGES
  );
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const isMultiBrand = useFeatureFlags(FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED);
  const brandId = useGetSelectedBrand(appKey);
  const {
    goal,
    setGoal,
    setLayout,
    layout,
    produceLayoutWithGoal,
    produceUpdatedAction,
    finalizeOffer,
  } = useContext(NewOfferFlowContext);

  const goals = useSelectNewOfferGoals();

  const [category, setCategoryState] = useState<OfferCategory>();
  const [objective, setObjective] = useState<OfferObjective>();
  const [presentation, setPresentation] = useState<OfferPresentation>();

  const processingOptsLocal = useProcessingOpts();

  const [processingOpt, setProcessingOpt] = useState<CtaProcessingOption[]>([
    processingOptsLocal[0]!.value,
  ]);

  const atPlayEditor = !!location.state?.fromPlayEditor;

  useEffect(() => {
    const navState = location.state;
    if (navState?.fromPlayEditor) {
      setObjective(navState?.objective);
      setPresentation(navState?.presentation);
    }
  }, [location]);

  //TODO AbK improve hardcoded category for gen AI
  useEffect(() => {
    if (aiFlow) {
      setCategory(OfferCategory.multi_action);
      setIsMultiAction(true);
    }
  });
  useEffect(() => {
    if (aiFlow && category) {
      setProcessingOpt([CtaProcessingOption.url]);
    }
  });
  useEffect(() => {
    if (aiFlow && category && processingOpt) {
      openEditor(aiFlow);
    }
  });

  const setCategory = (cat: OfferCategory | undefined) => {
    setCategoryState(cat);
    // reset to link if category doesn't allow billing processing
    if (
      processingOpt[0] === CtaProcessingOption.billing &&
      isDefined(cat) &&
      CATEGORIES_DONT_ALLOW_BILLING_PROCESSING.includes(cat)
    ) {
      setProcessingOpt((prevState) =>
        produce(prevState, (draft) => {
          draft[0] = CtaProcessingOption.url;
        })
      );
    }
    if (!cat) {
      setGoal(undefined);
    } else {
      const gLocal = goals.find((g) => g.category === cat);
      setGoal(gLocal);
    }
  };

  useEffect(() => {
    if (!category || category !== OfferCategory.offer_pack) return;
    if (category && goal && layout) {
      openEditor(aiFlow);
    }
  }, [category, goal, layout]);

  const [isMultiOffer, setIsMultiOffer] = useState(false);
  const [isMultiAction, setIsMultiAction] = useState(false);
  const [numberButtons, setNumberButtons] = useState(1);

  const [step, setStep] = useState<number>(1);
  useEffect(() => {
    if (!agsLicense) {
      if (isDefined(category)) setStep(2);
      else setStep(1);
    }
  }, [category]);

  useEffect(() => {
    if (isDefined(objective) && isUndefined(presentation)) setStep(2);
    else if (isDefined(objective) && isDefined(presentation)) setStep(3);
    else setStep(1);
  }, [objective, presentation]);

  //TODO: make objectives ags only options in next PR
  const objectiveCards = getObjectives(
    agsLicense
      ? [
          OfferObjective.acquisition,
          OfferObjective.upsell,
          OfferObjective.retention,
        ]
      : [
          OfferObjective.upsell,
          OfferObjective.churn_prevention,
          OfferObjective.churn_deflection,
        ]
  );

  const baseLayoutName = useMemo(() => {
    switch (presentation) {
      case OfferPresentation.banner: {
        return 'LCO Banner';
      }
      case OfferPresentation.modal: {
        return 'Image Card';
      }
      case OfferPresentation.pop_up: {
        return 'LCO Popup';
      }
      case OfferPresentation.card: {
        return 'Loss Aversion CTA Card';
      }
    }
    return 'Image Card';
  }, [presentation]);

  const getLayout = () => {
    const baseLayout = agsLicense ? baseLayoutName : 'Image Card';
    const layoutLocal: Layout = layouts.find((l) => l.name === baseLayout)!;
    const newLayoutWithOffer = {
      ...layoutLocal,
      offer: {
        ...produceLayoutWithGoal(layoutLocal),
      },
    };
    setLayout(newLayoutWithOffer);
  };

  useEffect(() => {
    if (!goal) return;
    getLayout();
  }, [goal]);

  const actionButton = useMemo(
    () => (props: ComponentPropsWithoutRef<'div'>) => (
      <div className="design-element" {...props}>
        {children}
      </div>
    ),
    [children]
  );

  function updateLayout(newPrimaryCard: RestModalCard) {
    const primaryCardIndex = layout?.offer.cards.findIndex(
      (c) => c.name === layout.offer.primary_card_name
    );
    if (primaryCardIndex === -1) return;
    const newLayout = produce(layout, (draft) => {
      if (!draft) return;
      set(draft, `offer.cards.${primaryCardIndex}`, newPrimaryCard);
    });
    if (!newLayout) return;
    setLayout(newLayout);
    return newLayout;
  }

  const actionLink = actions.find((a) => a.name === 'Link');

  const updateOfferCard = (primaryCard: RestModalCard) => {
    const action = actionLink;
    const newPrimaryCard = produce(primaryCard, (draft) => {
      if (!action) return;
      set(
        draft,
        'requires.forwards',
        produceUpdatedAction(primaryCard.requires?.forwards, action)
      );
    });
    const newLayout = updateLayout(newPrimaryCard);
    if (!newLayout) return;
    const layoutLocal = finalizeOffer(newLayout);
    let updatedOffer = produce(layoutLocal.offer, (draft) => {
      const draftPrimaryCard = draft.cards.find(
        (c) => c.name === draft.primary_card_name
      );
      if (!draftPrimaryCard) return;
      const primaryCardIdx = draft.cards.indexOf(draftPrimaryCard);
      if (primaryCardIdx !== -1 && numberButtons > 1) {
        for (let i = 0; i < numberButtons - 1; i += 1) {
          updateCtaAction(
            draft,
            ['cards', primaryCardIdx, 'requires', 'other_actions', i],
            RestModalAction.link,
            company?.domain
          );
        }
      }
      if (draftPrimaryCard) {
        const draftForwards = collectForwards(draftPrimaryCard);
        draftForwards.forEach((fw, idx) => {
          set(
            draftPrimaryCard,
            [...fw.path, 'cta_details_list'],
            [
              {
                processing_option: processingOpt[idx],
                category: isMultiOffer || isMultiAction ? undefined : category,
              } as CtaDetails,
              ...(isMultiAction
                ? [
                    {
                      processing_option: processingOpt[idx],
                      category:
                        isMultiOffer || isMultiAction ? undefined : category,
                    } as CtaDetails,
                  ]
                : []),
            ]
          );
        });
      }
    });
    return updatedOffer;
  };

  const openEditor = (aiFlow: boolean) => {
    if (!layout) return;
    const primaryCard = layout.offer.cards.find(
      (c) => c.name === layout.offer.primary_card_name
    ) as RestModalCard;
    if (!primaryCard) return;
    const isOfferPack = isOfferCardPack(layout.offer as RestOffer);
    let updatedOffer = isOfferPack
      ? layout.offer
      : updateOfferCard(primaryCard);
    if (!updatedOffer) return;
    updatedOffer = produce(updatedOffer, (draft) => {
      set(
        draft,
        'category',
        isMultiOffer
          ? OfferCategory.multi_cta
          : isMultiAction
            ? OfferCategory.multi_action
            : category
      );
      set(
        draft,
        'objective',
        objective ??
          (agsLicense ? OfferObjective.upsell : OfferObjective.retention)
      );
      if (!agsLicense) {
        const isModal = isModalTemplate(primaryCard.template);
        set(
          draft,
          'presentation',
          isModal ? OfferPresentation.modal : OfferPresentation.card
        );
      } else {
        set(draft, 'presentation', presentation ?? OfferPresentation.modal);
      }
      draft.display_name = isOfferPack
        ? `Annual, Monthly and Weekly plan${offerPackCount > 0 ? `(${offerPackCount})` : ''}`
        : draft.display_name;

      if (isMultiBrand) {
        draft.brand_id = brandId ?? DEFAULT_BRAND_ID;
        draft.shared = false;
      }
    });

    updatedOffer = replaceBrightbackWithDomainInOffer(
      updatedOffer,
      company.domain
    );

    const navigateToAiFlow =
      !!location.state?.fromPlayEditor && !!location.state?.aiFlow;

    history.push(
      createUrl(
        aiFlow || navigateToAiFlow
          ? AuthorizedPaths.offersEditAITopLevel
          : AuthorizedPaths.offersEdit,
        { offerId: undefined }
      ) + encodeObjectForQuery(searchParams),
      {
        ...location?.state,
        newOffer: updatedOffer,
        objective: updatedOffer.objective,
        presentation: updatedOffer.presentation,
        category: updatedOffer.category,
        aiFlow: aiFlow || navigateToAiFlow,
      }
    );
  };

  const offerPresentationOptions =
    objective === OfferObjective.churn_deflection
      ? [
          {
            title: 'Modal',
            helpText:
              'Show an offer in an overlay that dims background in the middle of page.',
            icon: Icons.image_modal,
            type: OfferPresentation.modal,
            disabled: false,
            show: true,
          },
          {
            title: 'Loss aversion card',
            helpText: 'Show an offer in an embedded container in your page.',
            icon: Icons.loss_aversion_card,
            type: OfferPresentation.card,
            disabled: false,
            show: true,
          },
        ]
      : [
          {
            title: 'Modal',
            helpText: 'Show an offer that blocks out the rest of the screen.',
            icon: Icons.modal,
            type: OfferPresentation.modal,
            disabled: false,
            show: true,
          },
          {
            title: 'Pop-up',
            helpText:
              'Show an offer that doesn’t  block out the rest of the screen.',
            icon: Icons.popup,
            type: OfferPresentation.pop_up,
            disabled: false,
            show: true,
          },
          {
            title: 'Banner',
            helpText:
              'Show an offer that appears above or below the screen’s content.',
            icon: Icons.banner,
            type: OfferPresentation.banner,
            disabled: false,
            show: true,
          },
          {
            title: 'Loss aversion card',
            helpText: 'Show an offer in an embedded container in your page.',
            icon: Icons.loss_aversion_card,
            type: OfferPresentation.card,
            disabled: false,
            show: objective === OfferObjective.retention,
          },
          {
            title: 'Offer card pack',
            helpText:
              'Show an offer in an embedded container in your offer pages.',
            icon: Icons.offerPack,
            type: OfferPresentation.card,
            disabled: false,
            show: enableHostedPages,
          },
        ];
  const shouldShowOfferCategory = (
    offerCategory: OfferCategory,
    objective: OfferObjective = OfferObjective.retention,
    presentation: OfferPresentation | undefined
  ): boolean => {
    const { categories, blockedPresentationsByCategory } =
      OFFER_OBJECTIVES_CATEGORY_MAPPING[objective] || {};

    if (!categories?.includes(offerCategory)) return false;
    if (!presentation) return true;

    const blockedPresentations =
      blockedPresentationsByCategory?.[offerCategory];

    return (
      !blockedPresentations ||
      blockedPresentations.length === 0 ||
      !blockedPresentations.includes(presentation!)
    );
  };

  const offerCategoryOptions = [
    {
      id: 1,
      title: 'Discount',
      helpText: 'A percentage (%) or absolute ($) discount.',
      type: OfferCategory.discount,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.discount,
        objective,
        presentation
      ),
    },
    {
      id: 2,
      title: 'Pause | Skip',
      helpText: 'Temporarily pause the subscription or skip upcoming cycles.',
      type: OfferCategory.pause,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.pause,
        objective,
        presentation
      ),
    },
    {
      id: 3,
      title: 'Trial Conversion',
      helpText: 'Turn trials into paying customers.',
      type: OfferCategory.trial_conversion,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.trial_conversion,
        objective,
        presentation
      ),
    },
    {
      id: 4,
      title: 'Extension',
      helpText: 'Extend the current term of the subscription.',
      type: OfferCategory.extension,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.extension,
        objective,
        presentation
      ),
    },
    {
      id: 5,
      title: 'Subscription change',
      helpText: 'Move to higher or lower plans or simply swap plans.',
      type: OfferCategory.plan_change,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.plan_change,
        objective,
        presentation
      ),
    },
    {
      id: 6,
      title: 'Support & training',
      helpText: 'Provide support and training assistance.',
      type: OfferCategory.support,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.support,
        objective,
        presentation
      ),
    },
    {
      id: 7,
      title: 'Customer feedback',
      helpText:
        'Collect feedback from subscribers to improve your current offering.',
      type: OfferCategory.customer_feedback,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.customer_feedback,
        objective,
        presentation
      ),
    },
    {
      id: 8,
      title: 'Gifts & credits',
      helpText: 'Provide a free gift or credits.',
      type: OfferCategory.gifts_and_credits,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.gifts_and_credits,
        objective,
        presentation
      ),
    },
    {
      id: 9,
      title: 'Cancel | Nevermind',
      helpText:
        'Offer primarily consists of cancel, nevermind, or dismiss actions.',
      type: OfferCategory.other,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.other,
        objective,
        presentation
      ),
    },
    {
      id: 10,
      title: 'Multi-offer',
      helpText: 'Provide multiple buttons with offers on a single card.',
      type: OfferCategory.discount,
      icon: function renderIcon() {
        return renderCategoryIcon(this.type);
      },
      show:
        shouldShowOfferCategory(
          OfferCategory.multi_cta,
          objective,
          presentation
        ) && presentation !== OfferPresentation.banner,
      action: () => {
        setIsMultiOffer(true);
        setNumberButtons(2);
        setProcessingOpt([
          processingOptsLocal[0]!.value,
          processingOptsLocal[0]!.value,
          processingOptsLocal[0]!.value,
        ]);
      },
    },
    {
      id: 11,
      title:
        objective === OfferObjective.acquisition
          ? 'Trial Conversion + Discount'
          : 'Multi-action',
      helpText: 'Combine multiple offers into a single button.',
      type: OfferCategory.multi_action,
      icon: function renderIcon() {
        return objective === OfferObjective.acquisition
          ? Icons.categoryTrialConversionPlusDiscount
          : renderCategoryIcon(this.type);
      },
      show: shouldShowOfferCategory(
        OfferCategory.multi_action,
        objective,
        presentation
      ),
      action: () => {
        setIsMultiAction(true);
        setProcessingOpt([
          processingOptsLocal[0]!.value,
          processingOptsLocal[0]!.value,
          processingOptsLocal[0]!.value,
        ]);
      },
    },
  ];

  const offerCategoryOrderForAcquisition = [
    OfferCategory.trial_conversion,
    OfferCategory.multi_action,
    OfferCategory.extension,
    OfferCategory.discount,
  ];

  const isOddOptions =
    offerCategoryOptions.filter((opt) => opt.show).length % 2 !== 0;

  const showChooseOfferCategory = !agsLicense
    ? isUndefined(category)
    : step == 3;
  const showChooseOfferObjective = agsLicense && isUndefined(objective);
  const showChooseOfferPresentation = agsLicense && step == 2;
  const showOfferProcessingOption = !agsLicense && step == 2;

  const title = (
    <>
      {showChooseOfferCategory && (
        <span
          className={agsLicense ? 'tw-block' : ''}
        >{`Choose your offer category`}</span>
      )}
      {showChooseOfferObjective && <>Choose your offer objective</>}
      {showChooseOfferPresentation && <>Choose your widget</>}
      {showOfferProcessingOption && <>Offer processing option</>}
    </>
  );

  const offerPresentation = (
    <div className="tw-flex tw-max-w-[400px] tw-flex-wrap tw-justify-evenly tw-p-3">
      {offerPresentationOptions
        .filter((op) => op.show)
        .map((option) => (
          <div className="tw-mb-5">
            <OfferPresentationCard
              title={option.title}
              helpText={option.helpText}
              icon={option.icon}
              onClick={() => {
                setPresentation(option.type);
                if (option.title == 'Offer card pack') {
                  setCategory(OfferCategory.offer_pack);
                  return;
                }
                setStep(3);
              }}
              disabled={option?.disabled}
            />
          </div>
        ))}
    </div>
  );

  const sortedOfferCategoryOptions =
    objective === OfferObjective.acquisition
      ? offerCategoryOptions
          .filter((option) => option.show)
          .sort((optionA, optionB) => {
            const aIndex = offerCategoryOrderForAcquisition.indexOf(
              optionA.type
            );
            const bIndex = offerCategoryOrderForAcquisition.indexOf(
              optionB.type
            );
            return aIndex - bIndex;
          })
      : offerCategoryOptions;

  if (aiFlow) return <></>;

  const slideHeight = agsLicense ? 'auto' : isMultiOffer ? '600px' : '500px';
  return (
    <GenericPopover
      popoverProps={{
        PaperProps: {
          style: {
            width: agsLicense
              ? step === 3 || step === 1
                ? '500px'
                : 'auto'
              : '500px',
            height: `${slideHeight}`,
          },
        },
        classes: {
          paper: 'bbk-generic-dialog-popover',
          root: 'tw-flex tw-justify-center tw-items-center',
        },
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'center',
        },
        disableEnforceFocus: true,
        anchorReference: 'none',
        open,
        transitionDuration: {
          enter: 200,
          exit: 0,
        },
      }}
      actionButton={actionButton}
      onClose={() => {
        setCategory(undefined);
        setIsMultiOffer(false);
        setNumberButtons(1);
        setObjective(undefined);
        setPresentation(undefined);
        onClose?.();
      }}
    >
      {({ handleClose }) => (
        <div className="tw-flex tw-h-full tw-flex-col">
          <div className="bbk-gdialog-header">
            <div className="bbk-gdialog-title">{title}</div>
            <div className="bbk-gdialog-header-actions">
              <ButtonV2 circle onClick={handleClose}>
                {Icons.closeProper}
              </ButtonV2>
            </div>
          </div>
          <div className="bbk-gdialog-body tw-flex tw-flex-col tw-justify-between tw-bg-white tw-text-cbrprimary-600">
            {showChooseOfferCategory && (
              <Slide in={showChooseOfferCategory} direction="left">
                <div>
                  <div
                    className={cs(
                      `tw-grid`,
                      {
                        'tw-grid-cols-3': isOddOptions,
                        'tw-grid-cols-2': !isOddOptions,
                      },
                      'tw-bg-white tw-text-center tw-text-xs tw-font-semibold tw-text-cbrprimary-600',
                      'tw-border tw-border-black/10'
                    )}
                  >
                    {sortedOfferCategoryOptions.map(
                      (cat) =>
                        cat.show && (
                          <TippyTooltip
                            tippyOptions={{
                              placement: 'top',
                            }}
                            content={cat.helpText}
                          >
                            <div
                              className={cs(
                                'tw-cursor-pointer tw-border-b tw-border-r tw-border-black/10 tw-py-6',
                                {
                                  'tw-border tw-border-solid tw-border-cbrprimary-300':
                                    category !== OfferCategory.discount
                                      ? cat.type === category
                                      : isMultiOffer
                                        ? cat.id === 10
                                        : cat.id === 1,
                                }
                              )}
                              onClick={() => {
                                setCategory(cat.type);
                                if (isMultiOffer || isMultiAction) {
                                  setIsMultiOffer(false);
                                  setIsMultiAction(false);
                                  setNumberButtons(1);
                                  setProcessingOpt([
                                    processingOptsLocal[0]!.value,
                                  ]);
                                }
                                cat?.action?.();
                              }}
                            >
                              <div className="tw-mb-1">{cat.icon()}</div>{' '}
                              {cat.title}
                            </div>
                          </TippyTooltip>
                        )
                    )}
                  </div>
                  {isMultiOffer && (
                    <fieldset className="tw-my-2">
                      <Label
                        css={css`
                          font-size: 14px;
                        `}
                      >
                        Number of buttons
                      </Label>
                      <GenericSelect
                        isSearchable={false}
                        options={numberButtonsOpts}
                        placeholder="Select..."
                        value={numberButtonsOpts.find(
                          (x) => x.value === numberButtons
                        )}
                        onChange={({ value }) => {
                          setNumberButtons(value);
                        }}
                      />
                    </fieldset>
                  )}
                  {agsLicense && category && (
                    <div className="tw-pt-6">
                      <ButtonV2
                        primary
                        className="tw-w-full"
                        onClick={() => openEditor(aiFlow)}
                      >
                        Create offer
                      </ButtonV2>
                    </div>
                  )}
                </div>
              </Slide>
            )}

            {showOfferProcessingOption && (
              <Slide direction="left" in={showOfferProcessingOption}>
                <div
                  className={cs('tw-flex tw-flex-col tw-justify-between', {
                    'tw-h-full': !isMultiOffer,
                  })}
                >
                  <div className="tw-text-sm">
                    <div className="tw-mb-4">
                      Configure how your{' '}
                      {numberButtons > 1 ? 'offers' : 'offer'} will be processed
                      when a customer accepts the offer. Please review your
                      selections as this will be used for our offer benchmarking
                      to improve performance.{' '}
                      <Link
                        to={{
                          pathname:
                            'https://www.chargebee.com/docs/retention/how-to-process-your-chargebee-retention-offers-in-billing.html',
                        }}
                        target="_blank"
                        className="tw-underline"
                      >
                        {externalLinkIcon}
                      </Link>
                    </div>
                    {isMultiOffer && (
                      <fieldset className="tw-mb-2">
                        <Label
                          css={css`
                            font-size: 14px;
                          `}
                        >
                          Number of buttons
                        </Label>
                        <GenericSelect
                          isSearchable={false}
                          options={numberButtonsOpts}
                          placeholder="Select..."
                          value={numberButtonsOpts.find(
                            (x) => x.value === numberButtons
                          )}
                          onChange={({ value }) => {
                            setNumberButtons(value);
                          }}
                        />
                      </fieldset>
                    )}
                    {[...Array(numberButtons)].map((b, idx) => (
                      <fieldset className="tw-mb-2" key={`button-${idx}`}>
                        <Label
                          css={css`
                            font-size: 14px;
                          `}
                        >
                          Choose button {numberButtons > 1 ? idx + 1 : ''} offer
                          processing option
                        </Label>
                        <GenericSelect
                          isSearchable={false}
                          options={processingOptsLocal.map((po) => {
                            const isBillingRestrictedByCategory =
                              category &&
                              CATEGORIES_DONT_ALLOW_BILLING_PROCESSING.includes(
                                category
                              );
                            const disabledByRestrictedCategory =
                              po.value === CtaProcessingOption.billing &&
                              isBillingRestrictedByCategory;
                            return {
                              ...po,
                              label: disabledByRestrictedCategory ? (
                                <Tooltip
                                  title={`"Customer Feedback" and "Support" categories don't allow processing via billing`}
                                  disableInteractive
                                >
                                  <div>{po.label}</div>
                                </Tooltip>
                              ) : (
                                po.label
                              ),
                              isDisabled:
                                po.isDisabled || disabledByRestrictedCategory,
                            };
                          })}
                          placeholder="Select option..."
                          value={processingOptsLocal.find(
                            (po) => po.value === processingOpt[idx]
                          )}
                          onChange={(opt) =>
                            setProcessingOpt((prevState) =>
                              produce(prevState, (draft) => {
                                draft[idx] = opt.value;
                              })
                            )
                          }
                          styles={{
                            menu: (provided: CSSObjectWithLabel) => ({
                              ...provided,
                              zIndex: 9999,
                              color: 'black',
                            }),
                          }}
                        />
                      </fieldset>
                    ))}
                  </div>

                  <ButtonV2
                    primary
                    className="tw-w-full"
                    onClick={() => openEditor(aiFlow)}
                  >
                    Create offer
                  </ButtonV2>
                </div>
              </Slide>
            )}
            {agsLicense && step == 1 && (
              <Slide direction="left" in={step === 1}>
                <div className={cs('tw-flex', 'tw-flex-wrap')}>
                  {objectiveCards.map((data) => (
                    <OfferObjectiveCard
                      data={data}
                      onClick={() => setObjective(data.value as OfferObjective)}
                    />
                  ))}
                </div>
              </Slide>
            )}
            {agsLicense && step == 2 && (
              <Slide direction="left" in={step === 2}>
                {offerPresentation}
              </Slide>
            )}
            {!atPlayEditor && (
              <div className="tw-flex tw-flex-col tw-items-center">
                <div className="tw-pt-3 tw-text-xs">
                  Step {step} of {agsLicense ? 3 : 2}
                </div>
                <div className="tw-mt-4 tw-flex tw-justify-center">
                  <div
                    onClick={() => {
                      if (agsLicense) {
                        setObjective(undefined);
                        setPresentation(undefined);
                        setStep(1);
                        return;
                      }
                      setCategory(undefined);
                      setIsMultiOffer(false);
                      setNumberButtons(1);
                    }}
                    className={cs(
                      'tw-mr-[5px] tw-h-[4px] tw-w-[60px] tw-cursor-pointer tw-rounded-full',
                      {
                        'tw-bg-cbrprimary-600/20': step != 1,
                        'tw-bg-cbrprimary-600/80': step == 1,
                      }
                    )}
                  />
                  <div
                    onClick={() => {
                      if (!objective) return;
                      if (agsLicense) {
                        setStep(2);
                      }
                      setCategory(undefined);
                      setIsMultiOffer(false);
                      setNumberButtons(1);
                    }}
                    className={cs(
                      'tw-mr-[5px] tw-h-[4px] tw-w-[60px] tw-cursor-pointer tw-rounded-full',
                      {
                        'tw-bg-cbrprimary-600/20': step != 2,
                        'tw-bg-cbrprimary-600/80': step == 2,
                      }
                    )}
                  />
                  {agsLicense && (
                    <div
                      className={cs(
                        'tw-mr-[5px] tw-h-[4px] tw-w-[60px] tw-rounded-full',
                        {
                          'tw-bg-cbrprimary-600/20': step != 3,
                          'tw-bg-cbrprimary-600/80': step == 3,
                        }
                      )}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </GenericPopover>
  );
};
